import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  collection, getDocs, doc, setDoc, updateDoc, deleteDoc, Timestamp
} from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';

const emptyForm = {
  code: '',
  type: 'percent',
  amount: '',
  active: true
};

function AdminDiscounts() {
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  // Load all discount codes from Firestore
  async function loadDiscounts() {
    setLoading(true);
    try {
      const snapshot = await getDocs(collection(db, 'discounts'));
      setDiscounts(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Error loading discounts:', err);
    }
    setLoading(false);
  }

  useEffect(() => {
    loadDiscounts();
  }, []);

  // Handle form field changes (including the checkbox for active)
  function handleChange(e) {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm({ ...form, [e.target.name]: value });
  }

  // Save a new discount code to Firestore
  // The document ID is the code itself so it can be looked up directly during checkout
  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);

    const code = form.code.trim().toUpperCase();
    if (!code) {
      setMessage('❌ Please enter a discount code.');
      setSaving(false);
      return;
    }

    const data = {
      type: form.type,
      amount: parseFloat(form.amount),
      active: form.active,
      updatedAt: Timestamp.now()
    };

    try {
      await setDoc(doc(db, 'discounts', code), data);
      setMessage(`✅ Code "${code}" saved!`);
      setShowForm(false);
      setForm(emptyForm);
      loadDiscounts();
    } catch (err) {
      setMessage('❌ Error saving discount code.');
      console.error(err);
    }

    setSaving(false);
  }

  // Toggle a code between active and inactive
  async function toggleActive(discount) {
    try {
      await updateDoc(doc(db, 'discounts', discount.id), {
        active: !discount.active
      });
      loadDiscounts();
    } catch (err) {
      console.error('Error toggling discount:', err);
    }
  }

  // Delete a discount code
  async function handleDelete(id) {
    if (!window.confirm(`Delete code "${id}"? This cannot be undone.`)) return;
    try {
      await deleteDoc(doc(db, 'discounts', id));
      setMessage(`✅ Code "${id}" deleted.`);
      loadDiscounts();
    } catch (err) {
      setMessage('❌ Error deleting discount code.');
      console.error(err);
    }
  }

  return (
    <div className="container py-5">

      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div>
          <Link to="/admin" className="text-muted small text-decoration-none">← Dashboard</Link>
          <h2 className="fw-bold mb-0">🏷️ Discount Codes</h2>
        </div>
        <button
          className="btn btn-piggy"
          onClick={() => { setShowForm(true); setForm(emptyForm); setMessage(''); }}
        >
          + New Code
        </button>
      </div>

      {message && (
        <div className="alert alert-info alert-dismissible fade show" role="alert">
          {message}
          <button type="button" className="btn-close" onClick={() => setMessage('')} />
        </div>
      )}

      {/* Create code form */}
      {showForm && (
        <div className="card shadow-sm border-0 mb-4">
          <div className="card-body p-4">
            <h5 className="fw-bold mb-3">➕ Create Discount Code</h5>
            <form onSubmit={handleSave}>
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label">Code *</label>
                  <input
                    name="code"
                    className="form-control text-uppercase fw-bold"
                    value={form.code}
                    onChange={handleChange}
                    placeholder="PORK10"
                    required
                  />
                  <div className="form-text">Stored as uppercase. Customers enter this at checkout.</div>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Discount Type *</label>
                  <select name="type" className="form-select" value={form.type} onChange={handleChange}>
                    <option value="percent">Percentage Off (%)</option>
                    <option value="flat">Flat Amount Off ($)</option>
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">
                    Amount ({form.type === 'percent' ? '%' : '$'}) *
                  </label>
                  <input
                    name="amount"
                    type="number"
                    step="0.01"
                    min="0"
                    className="form-control"
                    value={form.amount}
                    onChange={handleChange}
                    placeholder={form.type === 'percent' ? '10' : '5.00'}
                    required
                  />
                </div>
                <div className="col-12">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      id="activeCheck"
                      name="active"
                      className="form-check-input"
                      checked={form.active}
                      onChange={handleChange}
                    />
                    <label className="form-check-label" htmlFor="activeCheck">
                      Active — customers can use this code immediately
                    </label>
                  </div>
                </div>
              </div>
              <div className="mt-3 d-flex gap-2">
                <button type="submit" className="btn btn-piggy" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Code'}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setShowForm(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Discounts table */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" style={{ color: 'var(--piggy-pink)' }} role="status" />
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th>Code</th>
                <th>Type</th>
                <th>Discount</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {discounts.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center text-muted py-5">
                    No discount codes yet. Create one above!
                  </td>
                </tr>
              ) : (
                discounts.map(d => (
                  <tr key={d.id}>
                    <td><code className="fs-6 fw-bold">{d.id}</code></td>
                    <td>{d.type === 'percent' ? 'Percentage' : 'Flat Dollar'}</td>
                    <td className="fw-semibold text-pink">
                      {d.type === 'percent' ? `${d.amount}% off` : `$${d.amount} off`}
                    </td>
                    <td>
                      <span className={`badge ${d.active ? 'bg-success' : 'bg-secondary'}`}>
                        {d.active ? '✅ Active' : '⏸ Inactive'}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn btn-outline-secondary btn-sm me-2"
                        onClick={() => toggleActive(d)}
                      >
                        {d.active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => handleDelete(d.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default AdminDiscounts;
